import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Plus, Loader2 } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';

export const FeaturesTab = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await tl.listFeatures();
      setFeatures(data.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (body) => {
    setBusy(true);
    try {
      await tl.createFeature(body);
      setShowCreate(false);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]" data-testid="features-tab">
      <div className="col-span-5">
        <Card className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Feature Registry</h3>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="feature-new"><Plus className="w-3 h-3" /></Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1.5">
              {loading ? (
                <div className="text-center py-8 text-xs text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading
                </div>
              ) : features.length === 0 ? (
                <div className="text-center py-12 text-xs text-muted-foreground">
                  No features registered yet. Each feature maps to module / page / API / files / DB.
                </div>
              ) : (
                features.map((f) => (
                  <button key={f.feature_id} onClick={() => setSelected(f)}
                    className={`w-full text-left p-2.5 rounded border text-xs hover:border-violet-500/50 transition-colors ${selected?.feature_id === f.feature_id ? 'border-violet-500 bg-violet-500/5' : 'border-border'}`}
                    data-testid={`feature-row-${f.feature_id}`}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Badge variant="outline" className="text-[9px] h-4">{f.module}</Badge>
                      <Badge className="text-[9px] h-4 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-0">{f.current_status}</Badge>
                    </div>
                    <div className="font-medium">{f.feature_name}</div>
                    {f.description && <div className="text-[10px] text-muted-foreground line-clamp-2">{f.description}</div>}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>
      <div className="col-span-7">
        {selected ? <FeatureDetail feature={selected} /> : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Select a feature
          </Card>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl" data-testid="feature-create-dialog">
          <DialogHeader><DialogTitle>Register feature</DialogTitle></DialogHeader>
          <CreateFeatureForm onSubmit={create} disabled={busy} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CreateFeatureForm = ({ onSubmit, disabled }) => {
  const [form, setForm] = useState({
    module: '', feature_name: '', page: '', subpage: '', description: '',
    user_actions: '', related_api_routes: '', frontend_files: '', backend_files: '', database_collections: '', known_risks: '',
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const splitList = (s) => (s || '').split(/[,\n]/).map((x) => x.trim()).filter(Boolean);

  const submit = () => {
    onSubmit({
      module: form.module,
      feature_name: form.feature_name,
      page: form.page || undefined,
      subpage: form.subpage || undefined,
      description: form.description || undefined,
      user_actions: splitList(form.user_actions),
      related_api_routes: splitList(form.related_api_routes),
      frontend_files: splitList(form.frontend_files),
      backend_files: splitList(form.backend_files),
      database_collections: splitList(form.database_collections),
      known_risks: splitList(form.known_risks),
    });
  };

  return (
    <ScrollArea className="max-h-[60vh]">
      <div className="space-y-3 pr-3">
        <div className="grid grid-cols-2 gap-2">
          <Input value={form.module} onChange={(e) => upd('module', e.target.value)} placeholder="Module (e.g. finance)" data-testid="feature-module" />
          <Input value={form.feature_name} onChange={(e) => upd('feature_name', e.target.value)} placeholder="Feature name" data-testid="feature-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input value={form.page} onChange={(e) => upd('page', e.target.value)} placeholder="Page" />
          <Input value={form.subpage} onChange={(e) => upd('subpage', e.target.value)} placeholder="Subpage" />
        </div>
        <Textarea value={form.description} onChange={(e) => upd('description', e.target.value)} placeholder="Description" className="min-h-[60px] text-xs" />
        <Textarea value={form.user_actions} onChange={(e) => upd('user_actions', e.target.value)} placeholder="User actions (comma- or newline-separated)" className="min-h-[40px] text-xs" />
        <Textarea value={form.related_api_routes} onChange={(e) => upd('related_api_routes', e.target.value)} placeholder="API routes (e.g. POST /api/finance/income)" className="min-h-[40px] text-xs font-mono" />
        <Textarea value={form.frontend_files} onChange={(e) => upd('frontend_files', e.target.value)} placeholder="Frontend files" className="min-h-[40px] text-xs font-mono" />
        <Textarea value={form.backend_files} onChange={(e) => upd('backend_files', e.target.value)} placeholder="Backend files" className="min-h-[40px] text-xs font-mono" />
        <Textarea value={form.database_collections} onChange={(e) => upd('database_collections', e.target.value)} placeholder="Database collections" className="min-h-[40px] text-xs font-mono" />
        <Textarea value={form.known_risks} onChange={(e) => upd('known_risks', e.target.value)} placeholder="Known risks" className="min-h-[40px] text-xs" />
      </div>
      <DialogFooter className="mt-3">
        <Button onClick={submit} disabled={!form.module || !form.feature_name || disabled} data-testid="feature-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </ScrollArea>
  );
};

const FeatureDetail = ({ feature }) => (
  <Card className="h-full" data-testid="feature-detail">
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline">{feature.module}</Badge>
            <Badge>{feature.current_status}</Badge>
          </div>
          <h2 className="text-base font-semibold">{feature.feature_name}</h2>
          {feature.description && <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>}
        </div>
        <Field label="Page" value={[feature.page, feature.subpage].filter(Boolean).join(' › ')} />
        <ListField label="User actions" items={feature.user_actions} />
        <ListField label="API routes" items={feature.related_api_routes} mono />
        <ListField label="Frontend files" items={feature.frontend_files} mono />
        <ListField label="Backend files" items={feature.backend_files} mono />
        <ListField label="Database" items={feature.database_collections} mono />
        <ListField label="Known risks" items={feature.known_risks} />
      </div>
    </ScrollArea>
  </Card>
);

const Field = ({ label, value }) => value ? (
  <div>
    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{label}</div>
    <div className="text-xs">{value}</div>
  </div>
) : null;

const ListField = ({ label, items, mono }) => (items?.length || 0) > 0 ? (
  <div>
    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">{label}</div>
    <ul className={`text-xs space-y-0.5 ${mono ? 'font-mono' : ''}`}>
      {items.map((it, i) => <li key={i}>• {it}</li>)}
    </ul>
  </div>
) : null;

export default FeaturesTab;
